/**
 * Метка «прошлый визит» для сводки `/digest` (§8.1).
 *
 * По спеке метка живёт в `users.last_digest_at`, но этого поля сервер пока не
 * отдаёт (приложение, миграция 004). До его появления метка хранится локально —
 * по пользователю, чтобы на общем компьютере сводки не перемешались.
 *
 * Жёсткое правило спеки: метка сдвигается **только** по явному нажатию
 * «Открыть таблицу». Случайное открытие сводки дифф не обнуляет, поэтому
 * функция записи вызывается ровно из одного места.
 */

const PREFIX = 'bm.lastVisitAt.'
/** Сводкой встречаем, если с прошлого визита прошло больше трёх дней (§8.1). */
const GREET_AFTER_DAYS = 3
/** Метки нет — показываем изменения за неделю и говорим об этом прямо. */
const FALLBACK_DAYS = 7

const key = (userId: number): string => `${PREFIX}${userId}`

/** Записанная метка визита или null, если её ещё нет. */
export function getLastVisitAt(userId: number): string | null {
  try {
    const raw = localStorage.getItem(key(userId))
    if (!raw) return null
    return Number.isNaN(Date.parse(raw)) ? null : raw
  } catch {
    // Приватный режим: сводка переживёт, просто покажет окно по умолчанию.
    return null
  }
}

/** Начало окна сводки: метка визита, а если её нет — неделя назад. */
export function digestSince(userId: number): { since: string; exact: boolean } {
  const mark = getLastVisitAt(userId)
  if (mark) return { since: mark, exact: true }
  const fallback = new Date(Date.now() - FALLBACK_DAYS * 86_400_000)
  return { since: fallback.toISOString(), exact: false }
}

/** Сдвинуть метку. Вызывается только из «Открыть таблицу» (§8.1). */
export function markVisitOpened(userId: number, at: Date = new Date()): void {
  try {
    localStorage.setItem(key(userId), at.toISOString())
  } catch {
    // Не записалось — в следующий раз сводка покажет окно по умолчанию.
  }
}

/** Встречать ли этого человека сводкой: прошло больше трёх дней (§8.1). */
export function shouldGreetWithDigest(userId: number, now: Date = new Date()): boolean {
  const mark = getLastVisitAt(userId)
  if (!mark) return false
  const parsed = Date.parse(mark)
  if (Number.isNaN(parsed)) return false
  return now.getTime() - parsed > GREET_AFTER_DAYS * 86_400_000
}
